import { useTranslation } from 'react-i18next'

import { DeliveryFeesDeliveryChargeFragment } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { TileDetailsTableItem } from '@/modules/shared/components/tile/tile-items/TileDetailsTableItem'
import { useMoney } from '@/modules/shared/hooks/useMoney'

interface DeliveryFeeProps {
  deliveryCharge?: DeliveryFeesDeliveryChargeFragment | null
}

export function DeliveryFees({ deliveryCharge }: DeliveryFeeProps) {
  const { t } = useTranslation()
  const { formatMoney } = useMoney()
  const { minimumOrderValueExTax, deliveryChargeExTax, deliveryChargeTaxPercentage } = deliveryCharge || {}

  return (
    <>
      {deliveryCharge ? (
        <section className="my-2 border" data-testid="delivery-fees">
          <TileDetailsTableItem
            title={t('shopPage.cart.summary.details.0to', '{{ zero }} - {{ amount }}', {
              zero: formatMoney({ amount: 0, returnAsString: true }),
              amount: formatMoney({ amount: minimumOrderValueExTax, returnAsString: true }),
            })}
            value={
              <>
                {formatMoney({ amount: deliveryChargeExTax })}
                {deliveryChargeTaxPercentage
                  ? ` ${t('shopPage.cart.summary.details.underFeeTax', '(Plus {{ tax }}% Tax)', {
                      tax: deliveryChargeTaxPercentage,
                    })}`
                  : ''}
              </>
            }
            tooltip={t(
              'deliveryFees.0toDescription',
              'The delivery fee for any orders between {{ zero }} and {{ amount }}, excluding tax.',
              {
                zero: formatMoney({ amount: 0, returnAsString: true }),
                amount: formatMoney({ amount: minimumOrderValueExTax, returnAsString: true }),
              }
            )}
          />
          <TileDetailsTableItem
            title={t('shopPage.cart.summary.details.aboveFee', 'Above {{ amount }}', {
              amount: formatMoney({ amount: minimumOrderValueExTax, returnAsString: true }),
            })}
            value={t('general.freeDelivery', 'Free Delivery')}
            tooltip={t(
              'deliveryFees.aboveDescription',
              'The delivery fee for any orders above {{ amount }}, excluding tax.',
              {
                amount: formatMoney({ amount: minimumOrderValueExTax, returnAsString: true }),
              }
            )}
          />
        </section>
      ) : (
        <div className="mb-5 mt-2 rounded-md bg-gray-100 p-4 text-center text-sm" data-testid="supplier-details-no-fee">
          {t(
            'shopPage.cart.summary.details.noDeliveryFees',
            'The Supplier has not added any delivery fees to your Organisation in PurchasePlus. Please contact the Supplier using the details above to discuss delivery fees.'
          )}
        </div>
      )}
    </>
  )
}
